import React, { useState, useEffect } from 'react';
import {
  Modal, Button, Form, Table, Spinner, Alert,
} from 'react-bootstrap';
import ServiceOfferService from '../../../Services/ServiceOfferService';
import InclusionExclusionEditor from './InclusionExclusionEditor';

const DEFAULT_INCLUSIONS = [
  'VAT',
  'Professional driver service within agreed working hours',
  'Parking fees (standard locations)',
  'Business class vehicle preparation',
  'Bottled water replenishment',
  'English-speaking drivers',
  'Meet & greet service in the arrivals hall',
];

const DEFAULT_EXCLUSIONS = [
  'Extra hours beyond agreed package',
  'Special parking location requests',
];

function InclusionTemplateModal({
  show, onHide, onTemplatesChanged, currentInclusions, currentExclusions,
}) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // null = list view, object = edit form

  const fetchTemplates = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await ServiceOfferService.fetchTemplates();
      setTemplates(response?.data?.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      fetchTemplates();
      setEditing(null);
    }
  }, [show]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await ServiceOfferService.createOrUpdateTemplate(editing);
      await fetchTemplates();
      if (onTemplatesChanged) onTemplatesChanged();
      setEditing(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (template) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete template "${template.name}"?`)) return;
    setError('');
    try {
      await ServiceOfferService.deleteTemplate(template.Id);
      await fetchTemplates();
      if (onTemplatesChanged) onTemplatesChanged();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete template');
    }
  };

  const handleCreateNew = () => {
    setEditing({
      name: '',
      inclusions: [],
      exclusions: [],
    });
  };

  const handleSaveFromCurrent = () => {
    setEditing({
      name: '',
      inclusions: [...(currentInclusions || [])],
      exclusions: [...(currentExclusions || [])],
    });
  };

  const handleEdit = (template) => {
    setEditing({
      Id: template.Id,
      name: template.name,
      inclusions: [...(template.inclusions || [])],
      exclusions: [...(template.exclusions || [])],
    });
  };

  const renderListView = () => {
    if (loading) {
      return (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading templates...
        </div>
      );
    }

    if (templates.length === 0) {
      return (
        <div className="text-center text-muted py-4">
          <i className="fa fa-file-text-o d-block mb-2" style={{ fontSize: '1.5em' }} />
          No templates yet. Create one to get started.
        </div>
      );
    }

    return (
      <Table striped bordered hover size="sm">
        <thead>
          <tr>
            <th>Name</th>
            <th style={{ width: '100px' }}>Inclusions</th>
            <th style={{ width: '100px' }}>Exclusions</th>
            <th style={{ width: '120px' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {templates.map((t) => (
            <tr key={t.Id}>
              <td>{t.name}</td>
              <td className="text-center">{(t.inclusions || []).length}</td>
              <td className="text-center">{(t.exclusions || []).length}</td>
              <td>
                <Button variant="outline-primary" size="sm" className="me-1" onClick={() => handleEdit(t)}>
                  <i className="fa fa-pencil" />
                </Button>
                <Button variant="outline-danger" size="sm" onClick={() => handleDelete(t)}>
                  <i className="fa fa-trash" />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  const renderEditView = () => (
    <>
      <Form.Group className="mb-3">
        <Form.Label className="fw-semibold">Template Name</Form.Label>
        <Form.Control
          type="text"
          value={editing.name}
          onChange={(e) => setEditing({ ...editing, name: e.target.value })}
          placeholder="e.g. Business Class, Economy, Airport Transfer"
          autoFocus
        />
      </Form.Group>

      <InclusionExclusionEditor
        items={editing.inclusions}
        onChange={(items) => setEditing({ ...editing, inclusions: items })}
        label="Inclusions"
        placeholder="Select or type to add..."
        defaultOptions={DEFAULT_INCLUSIONS}
      />

      <InclusionExclusionEditor
        items={editing.exclusions}
        onChange={(items) => setEditing({ ...editing, exclusions: items })}
        label="Exclusions"
        placeholder="Select or type to add..."
        defaultOptions={DEFAULT_EXCLUSIONS}
      />
    </>
  );

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {/* eslint-disable-next-line no-nested-ternary */}
          {editing
            ? (editing.Id ? 'Edit Template' : 'New Template')
            : 'Manage Templates'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {editing ? renderEditView() : renderListView()}
      </Modal.Body>
      <Modal.Footer>
        {editing ? (
          <>
            <Button variant="secondary" onClick={() => setEditing(null)}>
              Back
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={saving || !editing.name.trim()}
            >
              {saving ? 'Saving...' : 'Save Template'}
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline-secondary" size="sm" onClick={handleSaveFromCurrent}>
              <i className="fa fa-floppy-o me-1" />
              Save Current As Template
            </Button>
            <Button variant="primary" onClick={handleCreateNew}>
              <i className="fa fa-plus me-1" />
              Create New
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default InclusionTemplateModal;
